import React from 'react';
import { Identity } from './types';
import { useTranslation } from 'react-i18next';
import { useIdentity } from './hooks/useIdentity';
import { useSingleTenantConfig } from './hooks/useTenantConfig';

export const useEolBarMessage = (
  pathName: string,
  search: string,
  enabled = true,
) => {
  const { t } = useTranslation();
  const { data: identity } = useIdentity({ enabled });
  const { value: isEolMessageEnabled } = useSingleTenantConfig(
    'enable.legacy.settlement.objects.eol.message',
    'true',
    {
      staleTime: 5 * 60 * 1000, // 5 minutes - EOL message config doesn't change frequently
      cacheTime: 15 * 60 * 1000, // 15 minutes - keep in memory longer for better UX
      refetchOnMount: false, // Don't refetch if cache is not expired
      refetchOnWindowFocus: false, // Don't refetch when window gains focus
    },
    enabled,
  );

  const EolBarMessage = {
    legacyAR: (
      <div dangerouslySetInnerHTML={{ __html: t('EolMessage.LegacyAR') }} />
    ),
  };

  const isISOff = (identity?: Identity) => {
    if (
      !identity?.permissions?.includes(
        'permission.InvoiceSettlementHarmonization',
      ) &&
      !identity?.permissions?.includes('permission.ARSettlement')
    ) {
      return true;
    }
    return false;
  };

  const isOldPaymentPage =
    pathName === '/apps/NewPayment.do' &&
    (!search.includes('method=') ||
      search.includes('method=create') ||
      search.includes('method=list') ||
      search.includes('method=view'));

  const isNewPaymentPage =
    isISOff(identity) && pathName.includes('/platform/apps/com_zuora/payment');

  const isOldRefundPage =
    pathName === '/apps/NewRefund.do' &&
    (!search.includes('method=') ||
      search.includes('method=create') ||
      search.includes('method=list') ||
      search.includes('method=view'));

  const isNewRefundPage =
    isISOff(identity) && pathName.includes('/platform/apps/com_zuora/refund');

  const isOldIAPage =
    pathName === '/apps/Adjustment.do' &&
    (!search.includes('method=') ||
      search.includes('method=createInvoiceAdjustment') ||
      search.includes('method=list') ||
      search.includes('method=view'));

  const isNewIAPage = pathName.includes(
    '/platform/apps/com_zuora/invoiceadjustment',
  );

  const isOldIIAPage =
    pathName === '/apps/InvoiceItemAdjustment.do' &&
    (!search.includes('method=') ||
      search.includes('method=createInvoiceItemAdjustment') ||
      search.includes('method=list') ||
      search.includes('method=view'));

  const isNewIIAPage = pathName.includes(
    '/platform/apps/com_zuora/invoiceitemadjustment',
  );

  const isOldCBAPage = pathName === '/apps/CreditBalanceAdjustment.do';

  if (
    isEolMessageEnabled === 'true' &&
    (isOldPaymentPage ||
      isNewPaymentPage ||
      isOldRefundPage ||
      isNewRefundPage ||
      isOldIAPage ||
      isNewIAPage ||
      isOldIIAPage ||
      isNewIIAPage ||
      isOldCBAPage)
  ) {
    return EolBarMessage.legacyAR;
  }

  return null;
};
